import sumBy from 'lodash/sumBy';
import { useTranslation } from 'react-i18next';
import { fCurrency } from '@/utils/formatNumber';
import { useTicketsSelected, usePromotions } from '@/store';
import useEventContext from '@/contexts/event/useEventContext';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Valida si el HTML está vacío después de limpiar tags
 */
function isEmptyHtml(html) {
  if (!html) return true;
  const cleaned = html
    .replace(/<[^>]+>/g, '') // Remover tags HTML
    .replace(/&nbsp;/g, '') // Remover &nbsp;
    .replace(/\s/g, ''); // Remover espacios en blanco
  return cleaned.length === 0;
}

/**
 * Formatea la descripción HTML para mostrar
 * - Reemplaza <br> por espacios
 * - Remueve tags HTML
 * - Trunca a 80 caracteres
 */
function formatDescription(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
    .substring(0, 80);
}

// =============================================================================
// HOOK FACHADA
// =============================================================================

/**
 * Hook fachada que encapsula:
 * - Acceso al contexto del evento (useEventContext)
 * - Reglas de negocio del .ai-context
 * - Lógica de presentación calculada
 *
 * @param {Object} params
 * @param {Object} params.ticket - Datos del tipo de boleto
 * @param {number} params.quantity - Cantidad seleccionada actualmente
 * @param {Function} params.onChange - Callback al cambiar cantidad
 * @returns {Object} - Datos calculados y handlers para el componente
 */
export function useTicketItemFacade({
  withMap,
  ticket,
  onChange,
  applyBundle = false,
  setShowFan2Fan = () => { },
} = {}) {
  const {
    event: {
      preferences: { currency, primaryColor, fee_breakdown: feeBreakdown },
      event,
    },
  } = useEventContext();

  const { t } = useTranslation();

  const { promotions } = usePromotions();

  const { ticketsSelected } = useTicketsSelected();

  const { onDeleteTicket } = useTicketsContext();

  const getZone = () => {
    if (ticket.seatsIoInfo) {
      const labels = ticket.seatsIoInfo;
      if (labels.parent) {
        return t('checkout.home.cart.seatSelected', {
          parent: labels.parent,
          own: labels.own,
        });
      } else {
        return t('checkout.home.cart.seatQuantity', {
          quantity: ticket.quantity,
          own: labels.own,
        });
      }
    }
    return t('checkout.home.cart.ticketsQuantity', {
      quantity: ticket.quantity,
    });
  };

  const quantity = ticketsSelected
    ? sumBy(
      ticketsSelected.filter(
        (t) => t.ticket_type_id === ticket.ticket_type_id,
      ),
      (o) => o.quantity,
    )
    : 0;

  // Calcula el total de boletos seleccionados sin considerar el ticket type id
  const globalQty = ticketsSelected.length > 0 ? ticketsSelected.reduce((sum, item) => sum + item.quantity, 0) : 0;

  const orderedSelectedsTickets = ticketsSelected
    .filter((t) => t?.ticket_type_id === ticket?.ticket_type_id)
    .sort((a, b) => {
      const aId = a.id.toUpperCase();
      const bId = b.id.toUpperCase();

      if (aId < bId) return -1;
      if (aId > bId) return 1;

      return 0;
    });

  // ===========================================================================
  // 1. DATOS DEL BOLETO
  // ===========================================================================

  const name = ticket?.name || '';
  const availability = ticket?.availability ?? 0;
  const maxPerSale = ticket?.max_per_sale ?? Infinity;
  const maxPrice = ticket?.max_price || ticket?.price || 0;
  const categoryColor =
    ticket?.category_color || ticket?.category?.color || primaryColor;
  const categoryName = ticket?.category_name || '';
  const description = ticket?.description || '';
  const hasFan2Fan = ticket?.fan?.ticket_quantity >= 1;
  const showFan2Fan = hasFan2Fan && availability < 1;
  const fan2fanUrl = ticket?.fan?.purchase_url;


  // ===========================================================================
  // 2. PRECIO
  // ===========================================================================

  const isFree = maxPrice === 0;
  const hasAdditionalFee = feeBreakdown === 'f';

  let price = isFree ? null : `${fCurrency(maxPrice)} ${currency}`;
  price = showFan2Fan
    ? `${fCurrency(ticket.fan.ticket_price)} ${currency}`
    : price;

  // ===========================================================================
  // 2.1 DESCUENTOS
  // ===========================================================================

  const hasDiscount =
    promotions?.valid_for_ticket_type_list?.includes(
      withMap ? ticket?.ticket_type_id : ticket?.id,
    ) && !showFan2Fan;

  const discountType = promotions?.discount_type; // 'percentage', 'flat', 'bundle'

  let priceWithDiscount = 0;
  const bundleRule = promotions?.bundle_rule;

  if (hasDiscount) {
    let discount = 0;

    if (discountType === 'flat') discount = promotions.discount_amount;

    if (discountType === 'percentage')
      discount = (maxPrice * promotions.discount_amount) / 100;

    if (discountType === 'bundle' && applyBundle) {
      const [total, payed] = promotions.bundle_rule.split('x');
      discount = maxPrice * (1 - payed / total);
    }

    const result = maxPrice - discount;

    priceWithDiscount = `${fCurrency(result < 0 ? 0 : result)} ${currency}`;
  }

  // ===========================================================================
  // 3. LÍMITE POR TIPO DE BOLETO
  // ===========================================================================

  const maxPerType = Math.min(maxPerSale, availability);

  // ===========================================================================
  // 4. ESTADOS DE DISPONIBILIDAD
  // ===========================================================================

  const isSoldOut = availability <= 0 && !hasFan2Fan;
  const canIncrement = quantity < maxPerType;
  const disableByBookingMax = globalQty >= event.max_tickets_per_booking;
  const canDecrement = quantity > 0;

  // ===========================================================================
  // 4.1 ESTADOS DE DISPONIBILIDAD PARA BUNDLES
  // ===========================================================================

  let outOfBundle = false;
  let leftTicketsToGetPromotion = 0;

  if (hasDiscount && discountType === 'bundle' && !showFan2Fan) {
    const [total, payed] = promotions.bundle_rule.split('x');
    const residual = quantity % total;
    leftTicketsToGetPromotion = total - residual;
    const outOfBundleTicket =
      residual > 0 ? orderedSelectedsTickets.slice(-residual) : [];

    outOfBundle = outOfBundleTicket.some((oob) => oob.id === ticket.id);
  }

  // ===========================================================================
  // 5. DESCRIPCIÓN
  // ===========================================================================

  const hasDescription = !isEmptyHtml(description);

  // ===========================================================================
  // 6. HANDLERS
  // ===========================================================================

  const handlers = (qty) => {
    if (showFan2Fan) {
      setShowFan2Fan({ open: true, link: fan2fanUrl });
      return;
    }

    if ((canDecrement || canIncrement) && onChange) {
      onChange(ticket, qty);
    }
  };

  const deleteTicketHandler = () => {
    onDeleteTicket(ticket);
  };

  // ===========================================================================
  // 7. VARIANTS
  // ===========================================================================

  const getVariant = (allowedVariants) => {
    let variant = 'default';

    if (hasDiscount && !showFan2Fan) variant = 'promotion';
    if (isSoldOut) variant = 'soldOut';
    if (showFan2Fan) variant = 'fan2fan';

    return allowedVariants.includes(variant) && !outOfBundle
      ? variant
      : 'default';
  };

  // ===========================================================================
  // 8. RETORNO: INTERFAZ PÚBLICA
  // ===========================================================================

  return {
    // ─────────────────────────────────────────────────────────────────────
    // Datos del boleto
    // ─────────────────────────────────────────────────────────────────────
    name,
    categoryColor,
    categoryName,
    getZone,
    fan2fanUrl,

    // ─────────────────────────────────────────────────────────────────────
    // Precio
    // ─────────────────────────────────────────────────────────────────────
    price,
    isFree,
    hasAdditionalFee,
    hasDiscount,
    discountType,
    priceWithDiscount,
    bundleRule,
    // ─────────────────────────────────────────────────────────────────────
    // Descripción
    // ─────────────────────────────────────────────────────────────────────
    hasDescription,
    ticketDescription: description,
    categoryName,
    // ─────────────────────────────────────────────────────────────────────
    // Variantes
    // ─────────────────────────────────────────────────────────────────────
    getVariant,

    // ─────────────────────────────────────────────────────────────────────
    // Estados
    // ─────────────────────────────────────────────────────────────────────
    quantity,
    maxPerType,
    isSoldOut,
    showFan2Fan,
    canIncrement,
    canDecrement,
    disableByBookingMax,
    outOfBundle,
    leftTicketsToGetPromotion,
    // ─────────────────────────────────────────────────────────────────────
    // Handlers
    // ─────────────────────────────────────────────────────────────────────
    handlers,
    deleteTicketHandler,
  };
}

export default useTicketItemFacade;
